import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { walletService, type WalletBalance } from '../services/walletService';
import { brandService } from '../services/brandService';
import * as Sentry from '@sentry/react';

interface EnrichedBalance extends WalletBalance {
    brandName: string;
    logoUrl?: string;
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

function getLogoUrl(logoUrl: string): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    const path = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
    return `${base}${path}`;
}

export default function Wallet() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [balances, setBalances] = useState<EnrichedBalance[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Watch for infinite loading spinners
    useEffect(() => {
        if (!isLoading) return;
        const timer = setTimeout(() => {
            Sentry.captureMessage("UI bloqueada en estado de carga: Componente Wallet", "warning");
        }, 10000);
        return () => clearTimeout(timer);
    }, [isLoading]);

    // Fetch wallet data
    useEffect(() => {
        const fetchBalance = async () => {
            try {
                const [balanceData, brandsData] = await Promise.all([
                    walletService.getBalance(),
                    brandService.getAll()
                ]);

                const merged: EnrichedBalance[] = (balanceData ?? []).map(b => {
                    const brand = brandsData.find(br => br.id === b.brandId);
                    return {
                        ...b,
                        brandName: brand?.name ?? 'Negocio Desconocido',
                        logoUrl: brand?.logoUrl
                    };
                });

                setBalances(merged);
            } catch (err) {
                console.error('Error fetching balance:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchBalance();
    }, []);

    return (
        <div className="flex flex-col">
            {/* Top Bar */}
            <div className="sticky top-0 z-10 bg-white/95 px-5 pb-3 pt-12 backdrop-blur-sm">
                <h1 className="text-2xl font-bold text-gray-800">¡Hola, {user?.name}! 👋</h1>
                <p className="text-sm text-gray-500 mt-1">Aquí tienes tus recompensas listas para usar.</p>
            </div>

            <div className="flex flex-col gap-5 px-5 pb-6">
                {/* Wallet Cards */}
                <div className="mt-2">
                    <h2 className="mb-4 text-lg font-semibold text-gray-800">
                        Mis carteras
                    </h2>

                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="flex animate-pulse items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                                >
                                    <div className="h-14 w-14 rounded-full bg-gray-200" />
                                    <div className="flex-1">
                                        <div className="mb-2 h-4 w-32 rounded bg-gray-200" />
                                        <div className="h-3 w-20 rounded bg-gray-100" />
                                    </div>
                                    <div className="h-6 w-16 rounded bg-gray-200" />
                                </div>
                            ))}
                        </div>
                    ) : balances.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                            <Store size={40} className="mx-auto mb-3 text-gray-300" />
                            <p className="text-sm text-gray-400">
                                Aún no tienes puntos en ningún negocio.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {balances.map((balance, index) => (
                                <div
                                    key={balance.brandId ?? index}
                                    onClick={() => navigate(`/wallet/${balance.brandId}`, { state: balance })}
                                    className="flex cursor-pointer items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all hover:border-indigo-100 hover:shadow-md active:scale-[0.98]"
                                >
                                    {/* Logo */}
                                    {balance.logoUrl ? (
                                        <img
                                            src={getLogoUrl(balance.logoUrl)}
                                            alt={balance.brandName}
                                            className="h-14 w-14 rounded-full border border-gray-100 object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
                                            <Store size={24} className="text-indigo-400" />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="flex-1">
                                        <p className="font-semibold text-gray-900">
                                            {balance.brandName}
                                        </p>
                                    </div>

                                    {/* Points + Chevron */}
                                    <div className="flex items-center gap-2">
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-indigo-600">
                                                {(balance.balance ?? 0).toLocaleString()}
                                            </p>
                                            <p className="text-xs text-gray-400">pts</p>
                                        </div>
                                        <ChevronRight size={18} className="text-gray-300" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
